import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

import { getSession, storeSession } from '../services/api/sessionApi';
import useDepartmentStore from '../stores/departmentStore';
import { STAFF_ROLES } from '../constants/staff';
import { ABDM_ROLE_DEFAULTS } from '../constants/abdmPermissions';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * PermissionContext — Session Permission Based Rendering + Scope
 * ═══════════════════════════════════════════════════════════════════════
 *
 * SINGLE SOURCE OF TRUTH for all permission and role-based rendering.
 *
 * Central Scope System:
 *   Roles like DOCTOR are "scoped" — they only see their own data.
 *   useScope() returns { isScoped, scopeLabel } so every component
 *   can adapt headings, filters, and wording automatically.
 *
 * Role → Permission Matrix:
 *   ADMIN:          Full access (unscoped)
 *   RECEPTIONIST:   CREATE_VISIT, VIEW_VISIT, VIEW_QUEUE, CANCEL_VISIT, ADD_REPORT (unscoped)
 *   DOCTOR:         VIEW_VISIT, APPROVE_VISIT, COMPLETE_VISIT, CANCEL_VISIT, VIEW_QUEUE, ... (scoped to self)
 *   NURSE:          VIEW_VISIT, VIEW_QUEUE, CREATE_VISIT, CANCEL_VISIT, ADD_NOTES, ADD_VITALS, ADD_SYMPTOMS, ADD_REPORT (unscoped)
 *   LAB_TECHNICIAN: VIEW_VISIT (unscoped)
 *   PHARMACY:       VIEW_VISIT (unscoped)
 *
 * Central Tab System:
 *   WORKFLOW_TABS defines all possible workflow tab actions.
 *   useRoleTabs() returns only the tabs the current user has permission for.
 *   Components should NEVER define their own tab arrays — always use useRoleTabs().
 *
 * Central View Routing:
 *   ROLE_VIEW_MAP defines which dashboard view each role sees.
 *   useRoleView() returns the view key for the current user's role.
 */

// ── Scoped Roles — these roles see only their own data ────────────────
const SCOPED_ROLES = {
  DOCTOR: { label: 'My', greeting: 'Doctor' },
  LAB_TECHNICIAN: { label: 'My', greeting: 'Lab' },
};

// ── Role → Permission Mapping ────────────────────────────────────────
// OFFLINE FALLBACK ONLY. Backend is the single source of truth and returns
// permissions in the session response. These are used only when the session
// does not include a permissions array (e.g. cached/stale session data).

const ROLE_PERMISSIONS = {
  ADMIN: [
    'CREATE_VISIT',
    'VIEW_VISIT',
    'APPROVE_VISIT',
    'COMPLETE_VISIT',
    'CANCEL_VISIT',
    'CHANGE_DATE',
    'VIEW_QUEUE',
    'MANAGE_QUEUE',
    'VIEW_VISIT_DASHBOARD',
    'MANAGE_STAFF',
    'VIEW_STAFF',
    'MANAGE_ONBOARDING',
    'FUTURE_DEVELOPMENT',
    'ADD_NOTES',
    'ADD_VITALS',
    'ADD_SYMPTOMS',
    'ADD_PRESCRIPTION',
    'ADD_REPORT',
    'MANAGE_APPOINTMENT',
    'MANAGE_RX_TEMPLATES',
    'MANAGE_FEES',
    'EXPORT_VISIT',
    'PRINT_TOKEN',
    'MANAGE_PROCEDURE_CATALOG',
    'MANAGE_PROCEDURE_RATES',
    'VIEW_PROCEDURE_CATALOG',
    // Phase 1 — Treatment Plans
    'ADD_PROCEDURE',
    'EXECUTE_SITTING',
    'APPROVE_SITTING',
    'COLLECT_PAYMENT',
    'APPLY_DISCOUNT',
    'GENERATE_RECEIPT_PDF',
    'VIEW_CLINIC_ACTIVE_PROCEDURES',
    'MANAGE_BRANDING',     // Rx Branding — admin only
    // Phase 3.1 — Multi-doctor referral
    'REFER_VISIT',
    // Lab module (admin manages the catalog + sees everything)
    'MANAGE_LAB_CATALOG',
    'VIEW_LAB_CATALOG',
    'ORDER_LAB',
    'EXECUTE_LAB_ORDER',
    'VIEW_LAB_QUEUE',
    'CANCEL_LAB_ORDER',
    'ASSIGN_LAB_ORDER',
    'RECORD_LAB_EXTERNAL',
    // ABHA / ABDM — all slugs (single source: constants/abdmPermissions.js)
    ...ABDM_ROLE_DEFAULTS[STAFF_ROLES.ADMIN],
    // EPIC A / A1 — Departments (admin manages + views)
    'MANAGE_DEPARTMENT',
    'VIEW_DEPARTMENT',
    // EPIC B / B2 — IPD config catalogs (bed categories / surgeries / OT rooms).
    // Offline fallback only — the session's backend permissions array is the
    // source of truth (dark orgs strip this slug server-side); catalog tabs and
    // routes are additionally gated on useAdmissionEnabled().
    'MANAGE_BED_CONFIG',
    // Day-1 IPD / S1 — Charge Items master (admin-only). Offline fallback
    // only, same caveat as MANAGE_BED_CONFIG above.
    'MANAGE_CHARGE_MASTER',
  ],
  RECEPTIONIST: [
    'CREATE_VISIT',
    'VIEW_VISIT',
    'CANCEL_VISIT',
    'VIEW_QUEUE',
    'ADD_REPORT',
    'MANAGE_APPOINTMENT',
    'PRINT_TOKEN',
    // Phase 1 — Treatment Plans
    'VIEW_PROCEDURE_CATALOG',
    'ADD_PROCEDURE',       // gated by catalog receptionist_allowed at backend
    'COLLECT_PAYMENT',
    'GENERATE_RECEIPT_PDF',
    // APPLY_DISCOUNT intentionally not granted — hard-blocked server-side too
    // Lab module — front desk orders walk-in labs + assigns at registration +
    // can cancel + record done-outside
    'ORDER_LAB',           // gated by catalog receptionist_allowed at backend
    'VIEW_LAB_QUEUE',
    'ASSIGN_LAB_ORDER',
    'CANCEL_LAB_ORDER',
    'RECORD_LAB_EXTERNAL',
    // ABHA / ABDM defaults (single source: constants/abdmPermissions.js)
    ...ABDM_ROLE_DEFAULTS[STAFF_ROLES.RECEPTIONIST],
    // EPIC A / A1 — Departments (read-only)
    'VIEW_DEPARTMENT',
    // TOKEN-UPDATE / T2 — role-aware queue-print sheet
    'EXPORT_VISIT',
    // ADMISSION-ACCESS ADD-1 — opens the IPD page (parity with backend)
    'VIEW_CLINICAL_RECORD',
  ],
  DOCTOR: [
    'CREATE_VISIT',
    'VIEW_VISIT',
    'APPROVE_VISIT',
    'COMPLETE_VISIT',
    'CANCEL_VISIT',
    'CHANGE_DATE',
    'VIEW_QUEUE',
    'VIEW_VISIT_DASHBOARD',
    'ADD_NOTES',
    'ADD_VITALS',
    'ADD_SYMPTOMS',
    'ADD_PRESCRIPTION',
    'VIEW_ALL_METADATA',
    'DELETE_METADATA',
    'VIEW_PATIENT_HISTORY',
    'UPLOAD_PRESCRIPTION',
    'ADD_REPORT',
    'MANAGE_APPOINTMENT',
    'MANAGE_RX_TEMPLATES',
    'GENERATE_PRESCRIPTION_PDF',
    // Phase 1 — Treatment Plans
    'VIEW_PROCEDURE_CATALOG',
    'ADD_PROCEDURE',
    'EXECUTE_SITTING',
    'APPROVE_SITTING',
    'COLLECT_PAYMENT',
    'APPLY_DISCOUNT',
    'GENERATE_RECEIPT_PDF',
    'VIEW_CLINIC_ACTIVE_PROCEDURES',
    // Phase 3.1 — Multi-doctor referral
    'REFER_VISIT',
    // Lab module — doctor advises lab tests + sees lab status (doesn't track) +
    // can cancel a lab they advised + record done-outside
    'ORDER_LAB',
    'CANCEL_LAB_ORDER',
    'VIEW_LAB_CATALOG',
    'VIEW_LAB_QUEUE',
    'RECORD_LAB_EXTERNAL',
    // EPIC A / A1 — Departments (read-only)
    'VIEW_DEPARTMENT',
    // TOKEN-UPDATE / T2 — role-aware queue-print sheet (server enforces
    // doctor self-scope on both /export and /print-summary)
    'EXPORT_VISIT',
    // ABHA / ABDM defaults (single source: constants/abdmPermissions.js)
    ...ABDM_ROLE_DEFAULTS[STAFF_ROLES.DOCTOR],
    // ADMISSION-ACCESS ADD-1 — opens the IPD page (parity with backend)
    'VIEW_CLINICAL_RECORD',
  ],
  NURSE: [
    'VIEW_VISIT',
    'VIEW_QUEUE',
    'CREATE_VISIT',
    'CANCEL_VISIT',
    'ADD_NOTES',
    'ADD_VITALS',
    'ADD_SYMPTOMS',
    'ADD_REPORT',
    'UPLOAD_PRESCRIPTION',
    'MANAGE_APPOINTMENT',
    // Phase 1 — Treatment Plans
    'VIEW_PROCEDURE_CATALOG',
    'ADD_PROCEDURE',       // gated by catalog nurse_allowed at backend
    'EXECUTE_SITTING',
    // EPIC A / A1 — Departments (read-only)
    'VIEW_DEPARTMENT',
    // ADMISSION-ACCESS ADD-1 — opens the IPD page (parity with backend)
    'VIEW_CLINICAL_RECORD',
  ],
  LAB_TECHNICIAN: [
    'VIEW_VISIT',
    'VIEW_QUEUE',
    'VIEW_LAB_QUEUE',
    'VIEW_LAB_CATALOG',
    'EXECUTE_LAB_ORDER',
    // Cancel a not-started order (sample not yet collected); backend gates the
    // "started" boundary and the receptionist issues any refund.
    'CANCEL_LAB_ORDER',
    // LAB-ASSIGN-2 — register a walk-in lab visit, reach the IPD picker +
    // order-lab dialog, grant on-credit IPD lab orders, and read departments.
    'CREATE_VISIT',
    'ACCESS_IPD',
    'GRANT_CREDIT',
    'VIEW_DEPARTMENT',
    // FIX-4 — the "+ Add Investigation" picker (RegistrationInvestigationsSection)
    // reads GET /api/organisation/procedures for the technician's full active
    // lab catalog. `VIEW_LAB_CATALOG` gates no route on the backend, and the
    // shared catalog list is the only source of lab tests, so the backend
    // grants this READ-ONLY slug (2 GET routes; no management screen). The
    // fallback mirrors the backend map (§10.1).
    'VIEW_PROCEDURE_CATALOG',
  ],
  PHARMACY: [
    'VIEW_VISIT',
  ],
};

// ── Central Workflow Tab Definitions ──────────────────────────────────
// Maps each tab to the permission required to see it.
// Components should use useRoleTabs() instead of defining their own arrays.

const WORKFLOW_TABS = [
  { key: 'vitals',       icon: '🌡️', label: 'Vitals',       action: 'ADD_VITALS',            permission: 'ADD_VITALS' },
  { key: 'symptoms',     icon: '🤒', label: 'Symptoms',     action: 'ADD_SYMPTOMS',          permission: 'ADD_SYMPTOMS' },
  { key: 'notes',        icon: '📝', label: 'Notes',        action: 'ADD_NOTES',             permission: 'ADD_NOTES' },
  { key: 'prescription', icon: '💊', label: 'Prescription', action: 'ADD_PRESCRIPTION',      permission: 'ADD_PRESCRIPTION' },
  { key: 'report',       icon: '📄', label: 'Report',       action: 'ADD_REPORT',            permission: 'ADD_REPORT' },
  { key: 'procedures',   icon: '💉', label: 'Procedures',   action: 'ADD_PROCEDURE',         permission: 'ADD_PROCEDURE' },
  { key: 'lab',          icon: '🧪', label: 'Lab',          action: 'lab',                   permission: 'VIEW_LAB_QUEUE' },
  { key: 'history',      icon: '📋', label: 'History',      action: 'VIEW_PATIENT_HISTORY',  permission: 'VIEW_PATIENT_HISTORY' },
];


// ── Central View Routing ──────────────────────────────────────────────
// Maps role → dashboard view key. SmartWorkflowWidget uses this
// instead of hardcoded if/else chains.

const ROLE_VIEW_MAP = {
  ADMIN:        'AdminView',
  DOCTOR:       'DoctorView',
  NURSE:        'NurseView',
  RECEPTIONIST: 'ReceptionistView',
  LAB_TECHNICIAN: 'LabTechView',
};


// ── Roles that can see ALL metadata entries (not just their own) ─────

const FULL_VISIBILITY_ROLES = ['ADMIN', 'DOCTOR'];

// ── Context ──────────────────────────────────────────────────────────

const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState(null);

  const [memberId, setMemberId] = useState(null);

  /**
   * Initialize permissions from session data.
   * Priority: session.role → deriveRoleFromSession() fallback.
   * Also stores member_id for scope-based filtering (e.g. doctor sees own visits).
   */
  const initPermissions = useCallback((sessionData) => {
    if (!sessionData) return;

    // Store member_id for scope filtering
    setMemberId(sessionData.member_id || null);

    // If session provides explicit permissions array, use them directly
    if (sessionData.permissions && Array.isArray(sessionData.permissions)) {
      setPermissions(sessionData.permissions);
      setRole(sessionData.role || null);
      return;
    }

    // Otherwise derive from role field (preferred) or fallback heuristics
    const userRole = sessionData.role || deriveRoleFromSession(sessionData);
    setRole(userRole);

    const rolePerms = ROLE_PERMISSIONS[userRole] || [];
    setPermissions(rolePerms);
  }, []);

  /**
   * Re-fetch the session and re-apply its permissions. Used after an org
   * setting that changes the user's resolved permissions WITHOUT a re-login —
   * e.g. enabling the Lab module, whose permission slugs the backend strips
   * from the session while the module is off. Best-effort: failures are logged,
   * not thrown, so the caller's flow (the toggle) isn't broken.
   */
  const refreshPermissions = useCallback(async () => {
    try {
      const data = await getSession();
      if (data) {
        storeSession(data);
        initPermissions(data);
      }
      return data;
    } catch (e) {
      console.warn('[PermissionContext] refreshPermissions failed:', e?.message || e);
      return null;
    }
  }, [initPermissions]);

  const value = useMemo(
    () => ({ permissions, role, memberId, initPermissions, refreshPermissions }),
    [permissions, role, memberId, initPermissions, refreshPermissions]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

/**
 * usePermission — Check if user has a specific permission
 *
 * @param {string} permission - Permission string to check
 * @returns {boolean} - True if user has the permission
 *
 * Usage:
 *   const canCreate = usePermission('CREATE_VISIT');
 *   if (canCreate) { ... }
 */
export function usePermission(permission) {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    console.warn('[usePermission] Used outside PermissionProvider');
    return false;
  }
  return ctx.permissions.includes(permission);
}

/**
 * useAnyPermission — true if the viewer holds ANY of the given slugs.
 *
 * The FE mirror of the backend's `require_any_permission(...)` dependency —
 * use this (never a manual `usePermission(a) || usePermission(b)`) wherever
 * a screen widens a gate to accept an alternate slug, e.g. C7/D-3's
 * "own admission slug OR ACCESS_IPD" (the IPD tile survives the per-member
 * Manage-Admission revoke; its own slugs don't). Keeps every widened FE gate
 * reading the SAME permission list a single API route accepts, so the two
 * can't drift apart the way QA-CTF-1 found them apart.
 *
 * @param {...string} slugs - Permission strings; any one match is enough.
 * @returns {boolean}
 */
export function useAnyPermission(...slugs) {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    console.warn('[useAnyPermission] Used outside PermissionProvider');
    return false;
  }
  return slugs.some((slug) => ctx.permissions.includes(slug));
}

/**
 * usePermissions — Access full permission context
 *
 * @returns {{ permissions: string[], role: string|null, hasPermission: (p: string) => boolean, initPermissions: (s: any) => void }}
 */
export function usePermissions() {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }

  const hasPermission = useCallback(
    (permission) => ctx.permissions.includes(permission),
    [ctx.permissions]
  );

  return {
    permissions: ctx.permissions,
    role: ctx.role,
    memberId: ctx.memberId,
    hasPermission,
    initPermissions: ctx.initPermissions,
    refreshPermissions: ctx.refreshPermissions,
  };
}

/**
 * useScope — Central scope hook for role-based UI adaptation
 *
 * Returns:
 *   isScoped     — true if user only sees their own data (e.g. DOCTOR)
 *   scopeLabel   — "My" for scoped, "" for unscoped (for headings)
 *   role         — the user's role
 *   memberId     — the user's member_id (for API calls)
 *   isAdmin      — true when the viewer is ADMIN/owner (unscoped, sees everyone)
 *   viewerDeptIds — the LOGGED-IN user's OWN department ids (EPIC A / A2). The
 *                   authoritative per-member set (deptsByMember[memberId]) — the
 *                   SAME source the server validates against. `undefined` while
 *                   not yet hydrated, `[]` for an unassigned (org-wide) viewer,
 *                   [ids] otherwise. Callers fail-open on `undefined` and treat
 *                   `[]` as "see everyone" via filterProvidersByViewerDepts.
 *
 * Usage in any component:
 *   const { isScoped, scopeLabel } = useScope();
 *   <h2>{scopeLabel} Visits</h2>       → "My Visits" for doctor, "Visits" for admin
 *   {!isScoped && <DoctorFilter />}    → hide doctor filter for scoped users
 */
export function useScope() {
  const ctx = useContext(PermissionContext);
  const memberId = ctx?.memberId || null;
  const role = ctx?.role || null;
  const isAdmin = String(role || '').toUpperCase() === 'ADMIN';

  // EPIC A / A2 — the viewer's OWN department set. Read the authoritative
  // member→dept edges (deptsByMember[memberId]) from the shared department store
  // (single source; the same map every dept-scoped picker consumes) and hydrate
  // them ONCE per member on demand. Admins are unscoped, so we don't fetch for
  // them. Selector subscription keeps the returned ids reactive as edges load.
  const viewerDeptEdges = useDepartmentStore(
    (s) => (memberId ? s.deptsByMember[memberId] : undefined),
  );
  const fetchMemberDepts = useDepartmentStore((s) => s.fetchMemberDepts);
  useEffect(() => {
    if (!memberId || isAdmin) return;
    if (viewerDeptEdges === undefined) fetchMemberDepts(memberId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, isAdmin, viewerDeptEdges, fetchMemberDepts]);

  // Memoize on the edges reference so callers depending on viewerDeptIds (e.g.
  // RegisterVisitForm's hydrate effect) get a STABLE array while the edges are
  // unchanged — a fresh array every render previously re-fired that effect →
  // hydrateAllMemberDepts storm (one request per department, hit the rate limit).
  const viewerDeptIds = useMemo(
    () =>
      Array.isArray(viewerDeptEdges)
        ? viewerDeptEdges.map((e) => e && (e.dept_id || e.id)).filter(Boolean)
        : undefined,
    [viewerDeptEdges],
  );

  if (!ctx) {
    return { isScoped: false, scopeLabel: '', role: null, memberId: null, isAdmin: false, viewerDeptIds: undefined };
  }

  const scopeConfig = SCOPED_ROLES[ctx.role];
  return {
    isScoped: !!scopeConfig,
    scopeLabel: scopeConfig?.label || '',
    role: ctx.role,
    memberId: ctx.memberId,
    isAdmin,
    viewerDeptIds,
  };
}

/**
 * useRoleTabs — Returns workflow tabs the current user has permission for.
 *
 * Replaces all hardcoded NURSE_TABS, RECEP_TABS, ADMIN_TABS, ALL_TABS.
 * Tabs are derived from WORKFLOW_TABS × user permissions.
 *
 * @param {object} [options]
 * @param {string[]} [options.exclude] — Tab keys to explicitly exclude (e.g. ['prescription'])
 * @returns {{ key, icon, label, action, permission }[]}
 *
 * Usage:
 *   const tabs = useRoleTabs();
 *   // Doctor → [vitals, symptoms, notes, prescription, report, history]
 *   // Nurse  → [vitals, symptoms, notes, report]
 *   // Receptionist → [report]
 */
export function useRoleTabs(options = {}) {
  const ctx = useContext(PermissionContext);
  const { exclude = [] } = options;

  return useMemo(() => {
    if (!ctx) return [];
    const perms = ctx.permissions || [];
    return WORKFLOW_TABS.filter(
      (tab) =>
        perms.includes(tab.permission) &&
        !exclude.includes(tab.key)
    );
  }, [ctx, exclude]);
}

/**
 * useRoleView — Returns the dashboard view key for the current user's role.
 *
 * @returns {string|null} — 'DoctorView', 'NurseView', 'AdminView', 'ReceptionistView', or null
 *
 * Usage:
 *   const viewKey = useRoleView();
 *   // 'DoctorView' for DOCTOR, 'NurseView' for NURSE, etc.
 */
export function useRoleView() {
  const ctx = useContext(PermissionContext);
  if (!ctx?.role) return null;
  return ROLE_VIEW_MAP[ctx.role] || null;
}

/**
 * hasRolePermission — Utility to check if a role has a permission (non-hook).
 * Used in non-component code like visitMetadata.js to derive roles[] from ROLE_PERMISSIONS.
 *
 * @param {string} role - Role string (e.g. 'ADMIN')
 * @param {string} permission - Permission string (e.g. 'ADD_NOTES')
 * @returns {boolean}
 */
export function hasRolePermission(role, permission) {
  const perms = ROLE_PERMISSIONS[role];
  return perms ? perms.includes(permission) : false;
}

/**
 * getRolesWithPermission — Returns all role names that have a given permission.
 * Used in visitMetadata.js to derive the roles[] array for each VISIT_ACTION.
 *
 * @param {string} permission - Permission string (e.g. 'ADD_NOTES')
 * @returns {string[]}
 */
export function getRolesWithPermission(permission) {
  return Object.entries(ROLE_PERMISSIONS)
    .filter(([, perms]) => perms.includes(permission))
    .map(([role]) => role);
}

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Derive role from session data when not explicitly provided.
 * Maps Cognito groups / agent types to internal roles.
 *
 * FAILS CLOSED (bk-793 / PATIENT-CARD F2). This used to `return 'ADMIN'` for
 * any org user it could not map — an authorisation decision defaulting OPEN,
 * i.e. exactly backwards. An unmappable session now derives to NO role
 * (`null`), which flows into `initPermissions` as
 * `ROLE_PERMISSIONS[null] || []` ⇒ the empty permission set.
 *
 * Why `null` and not the least-privileged EXISTING role (PHARMACY):
 *   - `null` is not a novel state. `role` is initialised to `null`
 *     (`useState(null)`) and is already set to `null` on the primary
 *     session path whenever the backend sends `permissions` without `role`
 *     (see initPermissions above), so every consumer already guards it —
 *     each one reads `(role || '').toUpperCase()`, `GREETING_ROLE_LABELS[role]
 *     || 'User'`, or `SCOPED_ROLES[role]`/`ROLE_VIEW_MAP[role]` (both plain
 *     lookups that yield undefined → null, the same result PHARMACY already
 *     produces since it has no ROLE_VIEW_MAP entry). Nothing white-screens.
 *   - PHARMACY would still GRANT something (`VIEW_VISIT`) and would label an
 *     unknown user "Pharmacist" in the dashboard greeting — a false identity
 *     on top of a privilege we never meant to hand out. `null` grants nothing.
 *
 * Deliberately UNCHANGED: an explicit `staff_role` still wins verbatim, and
 * the `agent === 'crowchat'` ⇒ ADMIN path stays. Both are real signals; note
 * that ACCOUNTANT and OTHER_STAFF are legitimate `staff_role`s that have no
 * ROLE_PERMISSIONS entry, so nulling "unmapped" staff_roles would lock them
 * out. Their permissions already come from the session's permissions array.
 *
 * @returns {string|null} role, or null when the session cannot be mapped.
 */
function deriveRoleFromSession(sessionData) {
  // Check for staff role in session
  if (sessionData.staff_role) {
    return sessionData.staff_role.toUpperCase();
  }

  // Check groups for role hints
  const groups = sessionData.groups || [];
  if (groups.some((g) => g.toLowerCase().includes('admin'))) return 'ADMIN';
  if (groups.some((g) => g.toLowerCase().includes('doctor'))) return 'DOCTOR';
  if (groups.some((g) => g.toLowerCase().includes('receptionist'))) return 'RECEPTIONIST';
  if (groups.some((g) => g.toLowerCase().includes('nurse'))) return 'NURSE';
  if (groups.some((g) => g.toLowerCase().includes('lab'))) return 'LAB_TECHNICIAN';
  if (groups.some((g) => g.toLowerCase().includes('pharmacy'))) return 'PHARMACY';

  // CrowGroup = org admin
  if (sessionData.agent === 'crowchat') return 'ADMIN';

  // No staff_role, no group match, not the crowchat agent — we do not know who
  // this is. Deny by default; never hand out ADMIN. (See the header note.)
  // Logged so a "my tiles vanished" report is diagnosable without a repro.
  console.warn('[PermissionContext] Session role could not be derived — denying by default');
  return null;
}

export { ROLE_PERMISSIONS, WORKFLOW_TABS, ROLE_VIEW_MAP, FULL_VISIBILITY_ROLES };
export default PermissionContext;
